import React, { useCallback, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, DateRangePicker } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { propertyService } from 'api/services/propertyService';
import { availabilityService } from 'api/services/availabilityService';
import type { AvailabilityResponse } from 'api/types/availability.types';

// ============================================================================
// TYPES
// ============================================================================

export interface StayData {
  propertyId: string;
  propertyName: string;
  propertyCode: string;
  checkInDate: string;
  checkOutDate: string;
}

interface StayDetailsSectionProps {
  data: StayData;
  onChange: (updates: Partial<StayData>) => void;
  fieldErrors: Record<string, string>;
  /** Disables the whole section (property + dates). */
  disabled?: boolean;
  /**
   * Booking being edited. Passed to the availability check so the booking's own
   * rooms are added back — otherwise it reads its own inventory as unavailable.
   */
  editingBookingId?: string;
  /**
   * Locks ONLY the property selector (edit mode) — dates stay editable.
   * Booking numbers are property-prefixed; property moves are out of scope.
   */
  propertyLocked?: boolean;
  /**
   * Backdated entry window (backdated_booking_max_days config): how many
   * days before today the check-in date may be. 0 = past dates disallowed.
   */
  backdatedMaxDays?: number;
  onAvailabilityResult: (result: AvailabilityResponse | null) => void;
  checkingAvailability: boolean;
  onCheckingChange: (checking: boolean) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

const StayDetailsSection: React.FC<StayDetailsSectionProps> = ({
  data, onChange, fieldErrors, disabled, propertyLocked, backdatedMaxDays = 0,
  onAvailabilityResult, checkingAvailability, onCheckingChange, editingBookingId,
}) => {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Property dropdown ---
  const loadPropertyOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 500, includeInactive: false });
    return res.data.map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  const handlePropertyChange = useCallback((val: string | string[] | null, _: any, option?: SelectOption | null) => {
    const id = (val as string) || '';
    const parts = option?.label?.split(' - ') || [];
    onChange({
      propertyId: id,
      propertyCode: parts[0]?.trim() || '',
      propertyName: parts.slice(1).join(' - ')?.trim() || '',
    });
  }, [onChange]);

  // --- Auto-check availability on property+dates change ---
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!data.propertyId || !data.checkInDate || !data.checkOutDate) {
      onAvailabilityResult(null);
      onCheckingChange(false);
      return;
    }

    // Validate dates before checking
    if (new Date(data.checkOutDate) <= new Date(data.checkInDate)) return;

    onCheckingChange(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await availabilityService.checkAvailability({
          propertyId: data.propertyId,
          checkInDate: data.checkInDate,
          checkOutDate: data.checkOutDate,
          numberOfRooms: 1,
          numAdults: 1,
          numChildren: 0,
          // Edit mode: add this booking's own rooms back, or it reads its own
          // inventory as unavailable.
          excludeBookingId: editingBookingId,
        });
        if (res.success && res.data) {
          onAvailabilityResult(res.data);
        } else {
          onAvailabilityResult(null);
        }
      } catch {
        onAvailabilityResult(null);
      } finally {
        onCheckingChange(false);
      }
    }, 500);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [data.propertyId, data.checkInDate, data.checkOutDate, onAvailabilityResult, onCheckingChange,
      // Read inside the request; stable per open in practice, but an
      // undeclared read is how a stale booking id gets sent.
      editingBookingId]);

  // --- Computed ---
  const numNights = data.checkInDate && data.checkOutDate
    ? Math.max(0, Math.floor((new Date(data.checkOutDate).getTime() - new Date(data.checkInDate).getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  const today = new Date().toISOString().split('T')[0];
  // Earliest selectable check-in: today minus the backdated window (local time).
  const minCheckIn = (() => {
    const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
    d.setDate(d.getDate() - Math.max(0, backdatedMaxDays));
    return d.toISOString().slice(0, 10);
  })();

  return (
    <div>
      <hr className="booking-section-divider" />
      <div className="booking-section-title">
        <IconSystem.calendar size={15} />
        Stay Details
        {checkingAvailability && (
          <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            Checking availability...
          </span>
        )}
      </div>

      {/* Property - full width */}
      <div style={{ marginBottom: '0.75rem' }}>
        <SyutSelect
          label={<>Property <span className="text-danger">*</span></>}
          isAsync
          loadOptions={loadPropertyOptions}
          defaultOptions={true}
          cacheOptions={false}
          value={data.propertyId}
          onChange={handlePropertyChange as any}
          placeholder="Select property..."
          size="md"
          isDisabled={disabled || propertyLocked}
          error={fieldErrors['propertyId']}
          menuPortalTarget={document.body}
        />
      </div>

      {/* Dates row */}
      <div className="form-grid-2">
        <DateRangePicker
          label={<>Check-in / Check-out <span className="text-danger">*</span></>}
          checkInDate={data.checkInDate}
          checkOutDate={data.checkOutDate}
          onChange={(checkIn, checkOut) => onChange({ checkInDate: checkIn, checkOutDate: checkOut })}
          minDate={minCheckIn}
          disabled={disabled}
          error={fieldErrors['checkInDate'] || fieldErrors['checkOutDate']}
          placeholder="Select check-in and check-out dates..."
        />
        <div>
          <label className="form-label">Nights</label>
          <input
            type="text"
            className="form-input"
            value={numNights > 0 ? `${numNights} night${numNights !== 1 ? 's' : ''}` : '-'}
            disabled
            style={{ background: 'var(--bg-tertiary)' }}
          />
        </div>
      </div>
    </div>
  );
};

export default StayDetailsSection;
